import React, { createContext, useContext } from 'react';
import { useUser, useClerk, useAuth as useClerkAuth } from '@clerk/clerk-react';

interface User {
  id: string;
  email: string;
  name?: string;
  image?: string;
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  checkout: (slug: string) => Promise<void>;
  changePlan: (slug: string) => Promise<boolean>;
  customerPortal: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user: clerkUser, isLoaded, isSignedIn } = useUser();
  const clerk = useClerk();
  const { getToken } = useClerkAuth();

  const user: User | null = clerkUser
    ? {
        id: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress || '',
        name: clerkUser.fullName ?? undefined,
        image: clerkUser.imageUrl ?? undefined,
      }
    : null;

  async function handleSignIn() {
    clerk.redirectToSignIn({ redirectUrl: '/dashboard' });
  }

  async function handleSignOut() {
    await clerk.signOut({ redirectUrl: '/login' });
  }

  async function handleCheckout(slug: string) {
    const baseUrl = window.location.origin;
    try {
      const token = await getToken();
      const res = await fetch(`${baseUrl}/api/polar/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ slug }),
      });
      const data: { url?: string } = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('[Auth] Checkout failed:', error);
    }
  }

  async function handleChangePlan(slug: string): Promise<boolean> {
    const baseUrl = window.location.origin;
    try {
      const token = await getToken();
      const res = await fetch(`${baseUrl}/api/polar/change-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ slug }),
      });
      if (!res.ok) {
        const data: { error?: string } = await res.json();
        console.error('[Auth] Change plan failed:', data.error);
        return false;
      }
      const data: { success?: boolean; redirect?: string } = await res.json();
      // Trialing users get redirected to Polar checkout for the new plan
      if (data.redirect) {
        window.location.href = data.redirect;
        return true; // Will navigate away
      }
      return true;
    } catch (error) {
      console.error('[Auth] Change plan failed:', error);
      return false;
    }
  }

  async function handleCustomerPortal() {
    const baseUrl = window.location.origin;
    try {
      const token = await getToken();
      const res = await fetch(`${baseUrl}/api/polar/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data: { url?: string } = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('[Auth] Portal failed:', error);
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!isSignedIn,
        isLoading: !isLoaded,
        signIn: handleSignIn,
        signOut: handleSignOut,
        checkout: handleCheckout,
        changePlan: handleChangePlan,
        customerPortal: handleCustomerPortal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
